import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  GraduationCap,
  HeartPulse,
  Landmark,
  Server,
  Warehouse,
  ScrollText,
} from "lucide-react";

import Header from "../components/Header";
import { Button } from "../components/ui/button";

const STEPS = [
  {
    n: "01",
    title: "Project brief",
    body: "Default or segment specification, applications, project type and country.",
  },
  {
    n: "02",
    title: "Architecture & functions",
    body: "Wired, wireless or hybrid. Occupancy, daylight, scenes and more.",
  },
  {
    n: "03",
    title: "Assembled specification",
    body: "Approved clauses assembled to your project. PDF or Word on demand.",
  },
];

const SEGMENTS = [
  { icon: ScrollText, name: "Default" },
  { icon: HeartPulse, name: "Healthcare" },
  { icon: GraduationCap, name: "Education" },
  { icon: Building2, name: "Office" },
  { icon: Landmark, name: "Defence" },
  { icon: Server, name: "Data Centre" },
  { icon: Warehouse, name: "Logistics" },
];

export default function LandingPage() {
  return (
    <div data-testid="landing-page" className="min-h-screen flex flex-col bg-white">
      <Header />

      {/* HERO */}
      <section className="relative hero-blueprint">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-[#e5e7eb] bg-white px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-[0.2em] font-semibold text-[#4b5563]">
              <span className="h-1.5 w-1.5 bg-[#c80f3f]" />
              Lighting Intelligence & Control
            </div>
            <h1
              data-testid="hero-headline"
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1f2a44] leading-[1.05]"
            >
              Generate a Project-Specific Lighting Control Specification in
              Minutes.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#4b5563] max-w-2xl leading-relaxed">
              Answer a short set of project questions and the tool assembles a
              tailored lighting control specification from Helvar’s approved
              clause library. Choose the Default Specification for a generic
              project, or a segment-specific specification where the building
              type is known. Approved clauses only - no invented content, no
              marketing copy.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/wizard">
                <Button
                  data-testid="start-builder-cta"
                  className="bg-[#1f2a44] hover:bg-[#161e31] text-white rounded-sm px-8 py-6 text-base font-medium"
                >
                  Start Specification Builder
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.75} />
                </Button>
              </Link>
              <a
                href="#how-it-works"
                data-testid="how-it-works-link"
                className="text-sm font-medium text-[#1f2a44] hover:text-[#c80f3f] transition-colors"
              >
                How it works ↓
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SEGMENTS */}
      <section className="border-t border-[#e5e7eb] bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[#c80f3f]">
              Default and segment specifications in minutes
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#1f2a44] tracking-tight">
              Build the lighting specification your project needs.
            </h2>
            <p className="mt-4 text-[#4b5563] leading-relaxed max-w-2xl">
              Start from the cross-segment Default Specification when the project
              is generic or unspecified, or pick a segment below for wording
              refined to that environment.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-7 gap-4">
            {SEGMENTS.map((s) => (
              <div
                key={s.name}
                data-testid={`segment-card-${s.name.toLowerCase()}`}
                className="bg-white border border-[#e5e7eb] rounded-sm p-8 flex flex-col items-center text-center"
              >
                <s.icon
                  className="h-8 w-8 text-[#1f2a44] mb-6"
                  strokeWidth={1.5}
                />
                <div className="font-display text-lg font-semibold text-[#1f2a44]">
                  {s.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="border-t border-[#e5e7eb] bg-[#f4f5f7]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[#c80f3f]">
                How it works
              </div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#1f2a44] tracking-tight">
                Your streamlined specification tool.
              </h2>
              <p className="mt-5 text-[#4b5563] leading-relaxed">
                Answer a short set of project questions and the tool produces
                a ready-to-issue specification in minutes. No manual clause
                hunting, no rewriting. Just tailored, approved text you can
                download as PDF or an editable Word document.
              </p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {STEPS.map((s) => (
                <div
                  key={s.n}
                  className="bg-white border border-[#e5e7eb] rounded-sm p-6"
                >
                  <div className="font-mono text-xs text-[#c80f3f]">
                    {s.n}
                  </div>
                  <div className="mt-4 font-display text-lg font-semibold text-[#1f2a44]">
                    {s.title}
                  </div>
                  <div className="mt-2 text-sm text-[#4b5563] leading-relaxed">
                    {s.body}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#e5e7eb] bg-[#1f2a44]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Ready to assemble your project specification?
            </h2>
            <p className="mt-3 text-white/70 max-w-2xl">
              Answer a short questionnaire. Review your inputs. Download the
              specification.
            </p>
          </div>
          <div className="md:justify-self-end">
            <Link to="/wizard">
              <Button
                data-testid="cta-start-bottom"
                className="bg-[#c80f3f] hover:bg-[#a10c32] text-white rounded-sm px-8 py-6 text-base font-medium"
              >
                Start now
                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.75} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
